import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Send } from "lucide-react";
import { api } from "../lib/api";
import { LANDING_PAGES } from "../seo/landingContent";
import { FaqBlock, faqLd } from "../components/site/FaqBlock";
import { LandingBuilders, LandingStages } from "../components/site/LandingBlocks";
import EnquiryDialog from "../components/site/EnquiryDialog";

const Steps = ({ steps }) => (
  <ol className="grid sm:grid-cols-2 gap-3 mt-6">
    {steps.map((s, i) => (
      <li key={i} className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5 flex gap-4">
        <span className="font-display text-2xl text-[#C9B79C] leading-none w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
        <span>
          <p className="text-[#F3EEE3] font-medium">{s.t}</p>
          <p className="text-[#F3EEE3]/65 text-sm mt-1.5 leading-relaxed">{s.d}</p>
        </span>
      </li>
    ))}
  </ol>
);

const Cards = ({ cards }) => (
  <div className="grid sm:grid-cols-2 gap-3 mt-6">
    {cards.map((c, i) => (
      <div key={i} className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
        <p className="font-display text-xl text-[#F3EEE3]">{c.t}</p>
        <p className="text-[#F3EEE3]/65 text-sm mt-2 leading-relaxed">{c.d}</p>
      </div>
    ))}
  </div>
);

export default function SeoLanding() {
  const { pathname } = useLocation();
  const page = LANDING_PAGES[pathname.replace(/\/+$/, "")];
  const [builders, setBuilders] = useState([]);
  const [stages, setStages] = useState([]);
  const [enq, setEnq] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (page?.showBuilders) api.get("/builders").then((r) => setBuilders(r.data || [])).catch(() => {});
    if (page?.showStages) api.get("/stages").then((r) => setStages(r.data || [])).catch(() => {});
  }, [pathname, page]);

  if (!page) return null;

  return (
    <div className="pt-[64px] bg-[#12201A] min-h-screen" data-testid={`landing-${pathname.slice(1)}`}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqLd(page.faq))}</script>
      </Helmet>

      <section className="relative overflow-hidden border-b border-[#ffffff14]">
        <img src={page.heroImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#12201A]/40 via-[#12201A]/70 to-[#12201A]" />
        <div className="relative max-w-[1100px] mx-auto px-5 md:px-8 pt-20 pb-16">
          <p className="eyebrow text-[#C9B79C] mb-4">{page.eyebrow}</p>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F3EEE3] leading-[1.02] max-w-3xl" data-testid="landing-h1">{page.h1}</h1>
          <p className="text-[#F3EEE3]/80 mt-6 max-w-2xl text-base md:text-lg leading-relaxed">{page.lede}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            {page.ctas.map((c, i) => (
              <Link
                key={c.to}
                to={c.to}
                data-testid={`landing-cta-${i}`}
                className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium transition-colors ${i === 0 ? "bg-[#F3EEE3] text-[#12201A] hover:bg-white" : "border border-[#F3EEE3]/40 text-[#F3EEE3] hover:bg-[#F3EEE3]/10"}`}
              >
                {c.label} <ArrowRight size={15} />
              </Link>
            ))}
            <button onClick={() => setEnq(true)} data-testid="landing-enquire-btn" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium border border-[#C9B79C]/50 text-[#C9B79C] hover:bg-[#C9B79C] hover:text-[#12201A] transition-colors">
              <Send size={14} /> Enquire
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="landing-stats">
          {page.stats.map((s, i) => (
            <div key={i} className="rounded-2xl border border-[#F3EEE3]/12 bg-[#0F1B15] p-5">
              <p className="font-display text-2xl md:text-3xl text-[#C9B79C] leading-tight">{s.value}</p>
              <p className="text-[#F3EEE3]/55 text-xs mt-2 leading-snug">{s.label}</p>
            </div>
          ))}
        </div>

        {page.sections.map((sec, i) => (
          <section key={i} className="mt-20" data-testid={`landing-section-${i}`}>
            <h2 className="font-display font-light text-3xl md:text-4xl text-[#F3EEE3]">{sec.title}</h2>
            {sec.body?.map((p, j) => (
              <p key={j} className="text-[#F3EEE3]/75 leading-relaxed mt-5 max-w-3xl">{p}</p>
            ))}
            {sec.steps && <Steps steps={sec.steps} />}
            {sec.cards && <Cards cards={sec.cards} />}
          </section>
        ))}

        {page.showStages && <LandingStages stages={stages} />}
        {page.showBuilders && <LandingBuilders builders={builders} />}
        <FaqBlock items={page.faq} testId="landing-faq" />

        <section className="mt-20 rounded-3xl border border-[#C9B79C]/30 bg-[#0F1B15] p-8 md:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="landing-final-cta">
          <div>
            <p className="eyebrow text-[#C9B79C] mb-3">Ready when you are</p>
            <h2 className="font-display font-light text-3xl md:text-4xl text-[#F3EEE3]">Find your lot, pick your builder, build your home.</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link to="/master-plan" className="inline-flex items-center justify-center gap-2 bg-[#F3EEE3] text-[#12201A] px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white transition-colors">Open master plan <ArrowRight size={15} /></Link>
            <button onClick={() => setEnq(true)} className="inline-flex items-center justify-center gap-2 border border-[#F3EEE3]/40 text-[#F3EEE3] px-7 py-3.5 rounded-full text-sm font-medium hover:bg-[#F3EEE3]/10 transition-colors"><Send size={14} /> Enquire</button>
          </div>
        </section>
      </div>

      <EnquiryDialog open={enq} onClose={() => setEnq(false)} context={{ label: page.h1, source: `landing:${pathname}` }} />
    </div>
  );
}
